import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import {
  FaPlus,
  FaList,
  FaTimes,
  FaCheck,
  FaExchangeAlt,
} from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const Contra = () => {
  const navigate = useNavigate();
  const [cashBankAccounts, setCashBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [filteredFromAccounts, setFilteredFromAccounts] = useState([]);
  const [filteredToAccounts, setFilteredToAccounts] = useState([]);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);

  const [contra, setContra] = useState({
    date: new Date().toISOString().split('T')[0],
    from_account: '',
    to_account: '',
    from_account_id: '',
    to_account_id: '',
    amount: '',
    description: '',
  });

  useEffect(() => {
    fetchCashBankAccounts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        fromDropdownRef.current &&
        !fromDropdownRef.current.contains(event.target)
      ) {
        setShowFromDropdown(false);
      }
      if (
        toDropdownRef.current &&
        !toDropdownRef.current.contains(event.target)
      ) {
        setShowToDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCashBankAccounts = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-accounts`,
        { account_type_ids: [9, 10] },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setCashBankAccounts(response.data.data);
      setFilteredFromAccounts(response.data.data);
      setFilteredToAccounts(response.data.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAlert({
        show: true,
        message: 'Error loading cash/bank accounts.',
        variant: 'danger',
      });
    }
  };

  const handleFromAccountChange = (e) => {
    const value = e.target.value;
    setContra({ ...contra, from_account: value, from_account_id: '' });

    if (value.trim()) {
      const filtered = cashBankAccounts.filter((acc) =>
        acc.name.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredFromAccounts(filtered);
      setShowFromDropdown(true);
    } else {
      setFilteredFromAccounts(cashBankAccounts);
      setShowFromDropdown(false);
    }
  };

  const handleToAccountChange = (e) => {
    const value = e.target.value;
    setContra({ ...contra, to_account: value, to_account_id: '' });

    if (value.trim()) {
      const filtered = cashBankAccounts.filter((acc) =>
        acc.name.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredToAccounts(filtered);
      setShowToDropdown(true);
    } else {
      setFilteredToAccounts(cashBankAccounts);
      setShowToDropdown(false);
    }
  };

  const selectFromAccount = (account) => {
    setContra({
      ...contra,
      from_account: account.name,
      from_account_id: account.id,
    });
    setShowFromDropdown(false);
  };

  const selectToAccount = (account) => {
    setContra({
      ...contra,
      to_account: account.name,
      to_account_id: account.id,
    });
    setShowToDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!contra.from_account_id || !contra.to_account_id) {
      setAlert({
        show: true,
        message: 'Please select both accounts',
        variant: 'warning',
      });
      return;
    }
    if (contra.from_account_id === contra.to_account_id) {
      setAlert({
        show: true,
        message: 'From Account and To Account cannot be the same.',
        variant: 'warning',
      });
      return;
    }
    if (!contra.amount || parseFloat(contra.amount) <= 0) {
      setAlert({
        show: true,
        message: 'Please enter a valid amount',
        variant: 'warning',
      });
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    const payload = {
      from_account_id: contra.from_account_id,
      to_account_id: contra.to_account_id,
      amount: parseFloat(contra.amount),
      date: contra.date,
      description: contra.description || '',
    };

    setLoading(true);
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}create-contra`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setAlert({
        show: true,
        message: `${
          response.data.message || 'Contra entry created successfully!'
        } Contra Number: ${response.data.contra_number}`,
        variant: 'success',
      });
      setContra({
        date: contra.date,
        from_account: '',
        to_account: '',
        from_account_id: '',
        to_account_id: '',
        amount: '',
        description: '',
      });
    } catch (error) {
      console.error('Error creating contra entry:', error);
      setAlert({
        show: true,
        message:
          error.response?.data?.message ||
          'Error creating contra entry. Please try again.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setContra({
      date: new Date().toISOString().split('T')[0],
      from_account: '',
      to_account: '',
      from_account_id: '',
      to_account_id: '',
      amount: '',
      description: '',
    });
    setAlert({ show: false, message: '', variant: '' });
  };

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '30px 20px',
        }}
      >
        {/* Header Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            color: 'white',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <div>
              <h4
                style={{
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <FaExchangeAlt style={{ fontSize: '1.8rem' }} />
                Create Contra Entry
              </h4>
              <p style={{ margin: '8px 0 0 0', opacity: 0.9 }}>
                Transfer funds between cash/bank accounts
              </p>
            </div>
            <Button
              variant="light"
              onClick={() => navigate('/contra-list')}
              style={{
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FaList /> View All Contras
            </Button>
          </div>
        </div>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Form Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            maxWidth: '800px',
            margin: '0 auto',
          }}
        >
          <Form onSubmit={handleSubmit}>
            {/* Row 1: Contra No. + Date */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}>
              <Form.Group style={{ flex: '1' }}>
                <Form.Label
                  style={{
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.9rem',
                  }}
                >
                  Contra No.
                </Form.Label>
                <Form.Control
                  value="New"
                  disabled
                  style={{
                    backgroundColor: '#f8f9fa',
                    color: '#6c757d',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    padding: '10px 15px',
                    fontStyle: 'italic',
                  }}
                />
              </Form.Group>
              <Form.Group style={{ flex: '1' }}>
                <Form.Label
                  style={{
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.9rem',
                  }}
                >
                  Date *
                </Form.Label>
                <Form.Control
                  type="date"
                  value={contra.date}
                  onChange={(e) =>
                    setContra({ ...contra, date: e.target.value })
                  }
                  required
                  style={{
                    borderRadius: '6px',
                    padding: '10px 15px',
                    border: '1px solid #dee2e6',
                    fontSize: '0.9rem',
                  }}
                />
              </Form.Group>
            </div>

            {/* Row 2: From Account + To Account */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}>
              <Form.Group style={{ flex: '1' }} ref={fromDropdownRef}>
                <Form.Label
                  style={{
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.9rem',
                  }}
                >
                  From Account (Cash/Bank) *
                </Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    value={contra.from_account}
                    onChange={handleFromAccountChange}
                    onFocus={() => setShowFromDropdown(true)}
                    placeholder="Search account..."
                    style={{
                      borderRadius: '6px',
                      padding: '10px 40px 10px 15px',
                      border: contra.from_account_id
                        ? '1px solid #28a745'
                        : '1px solid #dee2e6',
                      backgroundColor: contra.from_account_id
                        ? '#f0fff4'
                        : 'white',
                      fontSize: '0.9rem',
                    }}
                  />
                  {contra.from_account_id && (
                    <FaCheck
                      style={{
                        position: 'absolute',
                        right: '15px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#28a745',
                        fontSize: '1rem',
                      }}
                    />
                  )}
                  {showFromDropdown && filteredFromAccounts.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        marginTop: '5px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                      }}
                    >
                      {filteredFromAccounts.map((account) => (
                        <div
                          key={account.id}
                          onClick={() => selectFromAccount(account)}
                          style={{
                            padding: '10px 15px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            fontSize: '0.9rem',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = '#f8f9ff')
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = 'white')
                          }
                        >
                          {account.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Form.Group>

              <Form.Group style={{ flex: '1' }} ref={toDropdownRef}>
                <Form.Label
                  style={{
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.9rem',
                  }}
                >
                  To Account (Cash/Bank) *
                </Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    value={contra.to_account}
                    onChange={handleToAccountChange}
                    onFocus={() => setShowToDropdown(true)}
                    placeholder="Search account..."
                    style={{
                      borderRadius: '6px',
                      padding: '10px 40px 10px 15px',
                      border: contra.to_account_id
                        ? '1px solid #28a745'
                        : '1px solid #dee2e6',
                      backgroundColor: contra.to_account_id
                        ? '#f0fff4'
                        : 'white',
                      fontSize: '0.9rem',
                    }}
                  />
                  {contra.to_account_id && (
                    <FaCheck
                      style={{
                        position: 'absolute',
                        right: '15px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#28a745',
                        fontSize: '1rem',
                      }}
                    />
                  )}
                  {showToDropdown && filteredToAccounts.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        marginTop: '5px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                      }}
                    >
                      {filteredToAccounts.map((account) => (
                        <div
                          key={account.id}
                          onClick={() => selectToAccount(account)}
                          style={{
                            padding: '10px 15px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            fontSize: '0.9rem',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = '#f8f9ff')
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = 'white')
                          }
                        >
                          {account.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Form.Group>
            </div>

            {/* Amount Field - Prominent */}
            <Form.Group className="mb-4">
              <Form.Label
                style={{
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '1rem',
                }}
              >
                Amount *
              </Form.Label>
              <div style={{ position: 'relative' }}>
                <span
                  style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#667eea',
                    fontWeight: '700',
                    fontSize: '1.2rem',
                    zIndex: 1,
                  }}
                >
                  ₹
                </span>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={contra.amount}
                  onChange={(e) =>
                    setContra({ ...contra, amount: e.target.value })
                  }
                  onWheel={(e) => e.target.blur()}
                  required
                  placeholder="0.00"
                  style={{
                    borderRadius: '8px',
                    padding: '14px 16px 14px 40px',
                    border: '2px solid #667eea',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    backgroundColor: '#f8f9ff',
                    color: '#2c3e50',
                    outline: 'none',
                  }}
                />
              </div>
            </Form.Group>

            {/* Description */}
            <Form.Group className="mb-4">
              <Form.Label
                style={{
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.9rem',
                }}
              >
                Description
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={contra.description}
                onChange={(e) =>
                  setContra({ ...contra, description: e.target.value })
                }
                placeholder="Add notes or description (optional)"
                style={{
                  borderRadius: '6px',
                  padding: '10px 15px',
                  border: '1px solid #dee2e6',
                  fontSize: '0.9rem',
                  resize: 'vertical',
                }}
              />
            </Form.Group>

            {/* Action Buttons */}
            <div
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="outline-secondary"
                onClick={clearForm}
                disabled={loading}
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FaTimes /> Clear
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: '600',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" /> Creating...
                  </>
                ) : (
                  <>
                    <FaPlus /> Create Contra
                  </>
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Layout>
  );
};

export default Contra;
